import { useEffect, useMemo } from 'react';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Flex } from '@mantine/core';
import { TableContainer } from '@dealstack/frontend/shared/components/Documents/DocumentsTable/styled';
import { useManageFileAssociationStep } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/ManageFileAssociationStep/useManageFileAssociationStep';
import { DocumentTable } from '@dealstack/frontend/shared/components/Documents/DocumentTable/DocumentTable';
import { useMantineTableWithPaginationFilteringSorting } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { getAssociationFileColumns } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/ManageFileAssociationStep/getAssociationFileColumns';
import {
	LinkUploadContainer,
	MiddleContainer,
	SidePanelContainer,
} from '@dealstack/frontend/shared/components/Documents/UploadDocuments/ManageFileAssociationStep/styled';
import {
	Header,
	StepContainer,
	SubHeader,
	TextContainer,
} from '@dealstack/frontend/shared/components/Documents/UploadDocuments/styled';
import { TransactionInformationItem } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/ManageFileAssociationStep/components/TransactionInformationItem';
import {
	DATE_FORMATS,
	formatBEDateToUiWithFallback,
} from '@dealstack/shared/lib/format/dates/date';
import { FindRelatedFilesRes } from '@dealstack/shared/lib/generated';
import { ManageFileAssociationSkelton } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/ManageFileAssociationStep/components/ManageFileAssociationSkelton';
import { UseMantineTableDataOperationsPartialProps } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState';
import { ExistingFiles } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/ManageFileAssociationStep/components/ExistingFiles';
import {
	DocumentIdentity,
	UploadEntityDetails,
} from '@dealstack/frontend/shared/components/Documents/types';
import { groupMatchedDocuments } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/ManageFileAssociationStep/ManageFileAssociationStep.utils';
import { DOCUMENT_LIBRARY_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';

interface Props {
	relatedFilesInDeal: FindRelatedFilesRes[] | undefined;
	setRelatedFilesInDeal: (relatedFilesInDeal: FindRelatedFilesRes[]) => void;
	uploadEntityDetails?: UploadEntityDetails[];
	onViewExistingDocument?: (existingDocument: DocumentIdentity) => void;
}

/**
 * Deal association step when upload documents
 */
export const ManageFileAssociationStep = ({
	relatedFilesInDeal,
	setRelatedFilesInDeal,
	uploadEntityDetails,
	onViewExistingDocument,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const {
		setSelectedFile,
		relatedTransactions,
		linkedTransactions,
		selectedFile,
		toggleShouldLinkRelatedTransaction,
	} = useManageFileAssociationStep({
		relatedFilesInDeal,
		setRelatedFilesInDeal,
	});

	const matchedDocumentGroups = useMemo(
		() => groupMatchedDocuments(linkedTransactions ?? []),
		[linkedTransactions]
	);

	// Every linkable transaction was scoped by one MIP, so naming the first names them all.
	const mipName = relatedTransactions?.[0]?.mipName;

	const columns = getAssociationFileColumns({
		tCapManage,
	});
	const initialTableOperations: UseMantineTableDataOperationsPartialProps = {
		initialPagination: {
			pageIndex: 0,
			pageSize: 10,
		},
	};
	const { mantineTableDataOperations } = useMantineTableWithPaginationFilteringSorting(
		columns,
		initialTableOperations
	);

	useEffect(() => {
		if (!selectedFile && relatedFilesInDeal && relatedFilesInDeal?.length > 0) {
			const firstFile = relatedFilesInDeal[0];
			setSelectedFile(firstFile);
		}
	}, [relatedFilesInDeal, selectedFile, setSelectedFile]);

	if (!relatedFilesInDeal) {
		return <ManageFileAssociationSkelton />;
	}

	return (
		<StepContainer data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_FILE_ASSOCIATION_STEP}>
			<Flex w="100%">
				{uploadEntityDetails && uploadEntityDetails?.length > 0 && (
					<SidePanelContainer $borderDirection="right">
						<TextContainer>
							<Header>
								{tCapManage('document.link_duplicate_files.left_side_bar.header')}
							</Header>
							<SubHeader>
								{tCapManage(
									'document.link_duplicate_files.left_side_bar.sub_header'
								)}
							</SubHeader>
						</TextContainer>

						<LinkUploadContainer
							data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_SOURCE_RECORDS}
						>
							{uploadEntityDetails?.map((entity, index) => (
								<TransactionInformationItem
									key={`${entity.transactionId}_${index}`}
									buyerName={entity.buyer}
									sellerName={entity.seller}
									issuerName={entity.issuerName}
									securityName={entity.securityName}
									transactionDate={entity.transactionDate}
									transactionType={entity.transactionType}
									units={entity.units}
									price={entity.price}
									transactionId={entity.transactionId}
									securityIssuedIn={entity.securityIssuedIn}
									securityCurrency={entity.securityCurrency}
									securityUnitDenominator={entity.securityUnitDenominator}
									disabled={true}
									checked={true}
								/>
							))}
						</LinkUploadContainer>
					</SidePanelContainer>
				)}
				<MiddleContainer>
					<TableContainer>
						<DocumentTable<FindRelatedFilesRes>
							enableRenderBottomToolbarCustomActions={false}
							enableRowSelection={false}
							enablePagination={false}
							enableRowActions={false}
							dataOperations={mantineTableDataOperations}
							hasNoTopToolbar={true}
							onRowClick={(row) => {
								setSelectedFile(row.original);
							}}
							columns={columns}
							isLoading={false}
							data={relatedFilesInDeal}
						/>
					</TableContainer>
				</MiddleContainer>

				<SidePanelContainer $borderDirection="left">
					{!!matchedDocumentGroups.length && (
						<>
							<TextContainer>
								<Header>
									{tCapManage(
										'document.link_duplicate_files.right_side_bar.already.header'
									)}
								</Header>
								<SubHeader>
									{tCapManage(
										'document.link_duplicate_files.right_side_bar.already.sub_header'
									)}
								</SubHeader>
							</TextContainer>

							{matchedDocumentGroups.map((group) => (
								<ExistingFiles
									key={group.documentId}
									group={group}
									onViewExistingDocument={onViewExistingDocument}
								/>
							))}
						</>
					)}
					{!!relatedTransactions?.length && (
						<>
							<TextContainer>
								<Header>
									{tCapManage(
										'document.link_duplicate_files.right_side_bar.linkable.header',
										{ nbr_records: relatedTransactions.length }
									)}
								</Header>
								<SubHeader
									data-testid={
										DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_LINKABLE_RECORDS_DATE
									}
								>
									{tCapManage(
										'document.link_duplicate_files.right_side_bar.linkable.sub_header',
										{
											date: formatBEDateToUiWithFallback(
												relatedTransactions[0].transactionDate,
												DATE_FORMATS.CALENDAR_DATE
											),
										}
									)}
								</SubHeader>
								{!!mipName && (
									<SubHeader>
										{tCapManage(
											'document.link_duplicate_files.right_side_bar.linkable.mip_scope',
											{ mip_name: mipName }
										)}
									</SubHeader>
								)}
							</TextContainer>

							<LinkUploadContainer
								data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_LINKABLE_RECORDS}
							>
								{relatedTransactions?.map((entity, index) => (
									<TransactionInformationItem
										onClick={() => {
											toggleShouldLinkRelatedTransaction(
												entity.transactionId
											);
										}}
										key={`${entity.transactionId}_${index}`}
										buyerName={entity.buyerName}
										price={entity.cost}
										issuerName={entity.issuerName}
										securityName={entity.securityName}
										transactionDate={entity.transactionDate}
										transactionType={entity.transactionType}
										units={entity.units}
										disabled={false}
										checked={entity.shouldLink}
										transactionId={entity.transactionId}
										securityUnitDenominator={entity.securityUnitDenominator}
										securityIssuedIn={entity.securityIssuedIn ?? undefined}
										securityCurrency={entity.securityCurrency ?? undefined}
									/>
								))}
							</LinkUploadContainer>
						</>
					)}
				</SidePanelContainer>
			</Flex>
		</StepContainer>
	);
};
